import { Blocks, CloudSun, Waves, Sprout, Users, Briefcase, Compass, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { IMAGES } from "../config/site";

const Card = ({ id, icon: Icon, title, desc, img, span, delay }) => (
  <Reveal delay={delay} className={span}>
    <a
      href="#book"
      data-testid={id}
      className={`group relative flex h-full min-h-[15rem] flex-col justify-end overflow-hidden rounded-3xl border transition-transform duration-300 hover:-translate-y-1.5 ${
        img
          ? "border-[#064E3B]/10 text-[#FDFBF7]"
          : "border-[#064E3B]/12 bg-[#F7F4EB] p-7 text-[#1C2826] hover:shadow-[0_18px_40px_-18px_rgba(6,78,59,0.35)]"
      }`}
    >
      {img && (
        <>
          <img src={img} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#022C22]/95 via-[#022C22]/45 to-[#022C22]/10" />
        </>
      )}
      <div className={`relative ${img ? "p-7" : ""}`}>
        <span
          className={`mb-4 flex h-11 w-11 items-center justify-center rounded-2xl ${
            img ? "bg-[#FDFBF7]/15 text-[#A7F3D0] backdrop-blur-sm" : "bg-[#ECFDF5] text-[#064E3B]"
          }`}
        >
          <Icon size={22} />
        </span>
        <h3 className={`font-serif text-xl font-medium sm:text-2xl ${img ? "text-[#FDFBF7]" : "text-[#064E3B]"}`}>
          {title}
        </h3>
        <p className={`mt-2 max-w-md text-sm leading-relaxed ${img ? "text-[#FDFBF7]/80" : "text-[#526560]"}`}>
          {desc}
        </p>
        <span
          className={`mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.15em] ${
            img ? "text-[#A7F3D0]" : "text-[#059669]"
          }`}
        >
          Book for this
          <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </a>
  </Reveal>
);

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="grain bg-[#FDFBF7] py-20 lg:py-28">
      <div className="relative z-[2] mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <Reveal className="mb-12 max-w-2xl lg:mb-16">
          <Eyebrow>What I can help with</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl font-medium leading-snug tracking-tight text-[#064E3B] sm:text-4xl lg:text-5xl">
            Counselling that meets you <em className="text-[#059669]">where you are</em>
          </h2>
          <p className="mt-4 text-base text-[#526560]">
            Seven areas of care — for little ones finding their words, teens finding their footing,
            and grown-ups finding their calm.
          </p>
        </Reveal>
        <div data-testid="services-bento-grid" className="grid grid-cols-1 gap-6 md:grid-cols-12 lg:gap-8">
          <Card
            id="service-card-child-development"
            icon={Blocks}
            title="Child Development & Play Therapy"
            desc="Play is a child's first language. Through toys, stories and games, children learn to express feelings, build confidence and grow emotionally — without pressure."
            img={IMAGES.childDev}
            span="md:col-span-7"
            delay={0}
          />
          <Card
            id="service-card-anxiety"
            icon={CloudSun}
            title="Anxiety & Emotional Regulation"
            desc="Gentle, practical tools to understand worries, calm the body and steady the mind — for children and adults alike."
            span="md:col-span-5"
            delay={0.08}
          />
          <Card
            id="service-card-teen-counselling"
            icon={Compass}
            title="Teen Counselling"
            desc="Body image, exam stress, peer pressure and identity — a space where teens feel heard and understood, never lectured."
            span="md:col-span-4"
            delay={0}
          />
          <Card
            id="service-card-stress"
            icon={Waves}
            title="Stress & Coping Skills"
            desc="Learn simple, lasting ways to release everyday stress before it becomes overwhelm."
            span="md:col-span-4"
            delay={0.08}
          />
          <Card
            id="service-card-family-conflicts"
            icon={Users}
            title="Family Dynamics & Conflicts"
            desc="Helping families hear each other again — resolving conflicts, easing parenting stress and rebuilding connection."
            img={IMAGES.family}
            span="md:col-span-4"
            delay={0.16}
          />
          <Card
            id="service-card-adult-issues"
            icon={Sprout}
            title="Adult Issues & Self-Growth"
            desc="A safe, judgement-free space to untangle relationships, self-doubt, life transitions and personal growth."
            span="md:col-span-5"
            delay={0}
          />
          <Card
            id="service-card-work-stress"
            icon={Briefcase}
            title="Work Stress & Burnout"
            desc="For professionals running on empty — boundaries, balance and burnout recovery, one honest conversation at a time."
            img={IMAGES.anxiety}
            span="md:col-span-7"
            delay={0.08}
          />
        </div>
      </div>
    </section>
  );
}
